import { Box, TableCell } from "@mui/material";
import { useState } from "react";
import { Department, PaginatedListDepartment } from "../../../services/swagger";
import { DepartmentService } from "../../../services/api";
import { useNavigate } from "react-router";
import CreateDepartmentDialog from "../../organisms/CreateDepartmentDialog";
import { useTranslation } from "react-i18next";
import DataView, { QueryParameters } from "../../organisms/DataView";
import ItemCard from "../../organisms/ItemCard";
import { useQuery } from "@tanstack/react-query";

export default function DepartmentsPage() {
  const { t } = useTranslation("Department");
  const navigate = useNavigate();
  const [createDialog, setCreateDialog] = useState(false);
  const [query, setQuery] = useState<QueryParameters>();

  const loadDepartments = async (): Promise<PaginatedListDepartment | null> => {
    if (!query) return null;
    const res = await DepartmentService.getDepartmentList({
      search: query.search,
      limit: query.limit,
      skip: query.skip,
      filters: query.filters,
      sortDirection: query.sortDirection,
      sortField: query.sortField,
    });
    if (res?.data?.data) return res.data.data;
    else return null;
  };

  const { data: departments, isPlaceholderData: isLoading } = useQuery({
    queryFn: loadDepartments,
    queryKey: ["departments", { query }],
    refetchInterval: 1000 * 10,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    placeholderData: (previousData) => previousData,
    maxPages: 10,
  });

  const navigateToDepartment = (department: Department) => {
    navigate(`/m/department/${department._id}`);
  };

  const handleDataTableChange = (query: QueryParameters) => {
    setQuery(query);
  };

  return (
    <Box height={1}>
      <CreateDepartmentDialog
        open={createDialog}
        onClose={() => setCreateDialog(false)}
      />
      <DataView
        name={t("Departments")}
        onItemClick={(item) => navigateToDepartment(item)}
        headers={[
          { field: "name", name: t("Name"), type: "string" },
          { field: "description", name: t("Description"), type: "string" },
          {
            field: "administrative",
            name: t("Administrative"),
            type: "boolean",
          },
          {
            field: "users",
            name: t("Users"),
            type: "other",
            renderRowCell: (row: Department) => (
              <TableCell>{row.users.length}</TableCell>
            ),
          },
        ]}
        onChange={handleDataTableChange}
        items={departments?.docs || []}
        count={departments?.count || 0}
        renderItem={(department) => (
          <ItemCard
            department={department}
            onClick={() => navigateToDepartment(department)}
          />
        )}
        pagination
        queryFunction={DepartmentService.getDepartmentListQuery}
        button={{
          text: t("NewDepartment"),
          onClick: () => setCreateDialog(true),
        }}
        loading={isLoading || !departments}
      />
    </Box>
  );
}
